import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Target, Calendar, TrendingUp, Zap, Plus } from 'lucide-react';
import ChatInterface from './ChatInterface';
import GoalTracker from './GoalTracker';
import DailyCheckin from './DailyCheckin';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${API_BASE}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');
  const [currentChatSession, setCurrentChatSession] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [dailyAffirmation, setDailyAffirmation] = useState('');
  const [showCheckin, setShowCheckin] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    // Check if user just completed authentication
    const authCompleted = localStorage.getItem('auth_completed');
    if (authCompleted && Date.now() - parseInt(authCompleted) < 5000) { // Within 5 seconds
      setShowWelcome(true);
      // Remove the flag after showing welcome
      setTimeout(() => {
        localStorage.removeItem('auth_completed');
        setShowWelcome(false);
      }, 3000);
    }

    loadUserStats();
    loadDailyAffirmation();
    checkDailyCheckin();
  }, []);

  const loadUserStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats`);
      setUserStats(response.data);
    } catch (error) {
      console.error('Failed to load user stats:', error);
    }
  };

  const loadDailyAffirmation = async () => {
    try {
      const response = await axios.get(`${API}/ai/affirmation`);
      setDailyAffirmation(response.data.affirmation);
    } catch (error) {
      console.error('Failed to load affirmation:', error);
    }
  };

  const checkDailyCheckin = async () => {
    try {
      const response = await axios.get(`${API}/checkins?limit=1`);
      const today = new Date().toDateString();
      const lastCheckin = response.data[0];
      
      if (!lastCheckin || new Date(lastCheckin.date).toDateString() !== today) {
        setShowCheckin(true);
      }
    } catch (error) {
      console.error('Failed to check daily checkin:', error);
    }
  };

  const handleNewChatSession = (sessionId) => {
    setCurrentChatSession(sessionId);
  };

  const handleCheckinComplete = () => {
    setShowCheckin(false);
    loadUserStats(); // Refresh stats after checkin
  };

  const tabs = [
    { id: 'chat', label: 'AI Coach', icon: Zap },
    { id: 'goals', label: 'Goals', icon: Target },
    { id: 'checkin', label: 'Check-in', icon: Calendar },
    { id: 'progress', label: 'Progress', icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen bg-jet-black">
      {/* Welcome Back Animation */}
      {showWelcome && (
        <div className="fixed inset-0 bg-jet-black/95 flex items-center justify-center z-50 animate-fade-in">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="relative mb-8">
              <div className="w-32 h-32 bg-gradient-primary rounded-full flex items-center justify-center mx-auto animate-pulse-power">
                <span className="text-white font-black text-5xl">✓</span>
              </div>
              <div className="absolute inset-0 border-4 border-transparent border-t-neon-green border-r-electric-blue rounded-full animate-spin"></div>
            </div>
            <h1 className="display-hero mb-4 animate-slide-power">
              WELCOME BACK!
            </h1>
            <p className="text-energy text-xl animate-slide-power delay-200 mb-4">
              Your AI coach is ready to help you <span className="text-neon-green">DOMINATE TODAY</span>
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
              <span className="w-2 h-2 bg-neon-green rounded-full animate-pulse"></span>
              <span>Authentication successful • Loading your dashboard...</span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white uppercase tracking-wide">
                  COACH<span className="text-neon-green">AI</span>
                </h1>
                <p className="text-gray-300 text-sm">
                  Welcome back, <span className="text-neon-green font-bold">{user?.name}</span>!
                </p>
              </div>
            </div>

            <button
              onClick={logout}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-full transition-colors text-white"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Daily Affirmation */}
        {dailyAffirmation && (
          <div className="mb-8 bg-gradient-to-r from-electric-blue/10 to-neon-green/10 rounded-2xl p-6 border border-electric-blue/20">
            <h2 className="text-white font-bold text-lg mb-3 flex items-center">
              <Zap className="w-5 h-5 mr-2 text-neon-green" />
              TODAY'S POWER BOOST
            </h2>
            <p className="text-white text-lg font-medium italic">{dailyAffirmation}</p>
          </div>
        )}

        {/* Stats Cards */}
        {userStats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="text-3xl font-black text-neon-green mb-2">
                {userStats.total_sessions}
              </div>
              <div className="text-gray-400 text-sm uppercase tracking-wide">
                Coaching Sessions
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="text-3xl font-black text-electric-blue mb-2">
                {userStats.completed_goals}/{userStats.total_goals}
              </div>
              <div className="text-gray-400 text-sm uppercase tracking-wide">
                Goals Completed
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="text-3xl font-black text-neon-green mb-2">
                {userStats.streak_days}
              </div>
              <div className="text-gray-400 text-sm uppercase tracking-wide">
                Day Streak
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border border-gray-700">
              <div className="text-3xl font-black text-electric-blue mb-2">
                {userStats.current_energy_avg.toFixed(1)}/10
              </div>
              <div className="text-gray-400 text-sm uppercase tracking-wide">
                Avg Energy
              </div>
            </div>
          </div>
        )}

        {/* Daily Check-in Notification */}
        {showCheckin && (
          <div className="mb-6 bg-gradient-to-r from-neon-green/20 to-electric-blue/20 rounded-2xl p-4 border border-neon-green/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-neon-green" />
                <div>
                  <h3 className="text-white font-bold">Ready for your daily check-in?</h3>
                  <p className="text-gray-300 text-sm">Share your progress and get personalized guidance!</p>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('checkin')}
                className="btn-power px-6 py-2"
              >
                CHECK IN
              </button>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex space-x-2 mb-8 bg-gray-900 rounded-2xl p-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl font-bold text-sm uppercase tracking-wide transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-primary text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">
          {activeTab === 'chat' && (
            <div className="h-[600px]">
              <ChatInterface 
                sessionId={currentChatSession}
                onNewSession={handleNewChatSession}
              />
            </div>
          )}

          {activeTab === 'goals' && (
            <GoalTracker onStatsUpdate={loadUserStats} />
          )}

          {activeTab === 'checkin' && (
            <DailyCheckin 
              onComplete={handleCheckinComplete}
              onStatsUpdate={loadUserStats}
            />
          )}

          {activeTab === 'progress' && (
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700">
              <h2 className="heading-bold text-white mb-6">YOUR TRANSFORMATION JOURNEY</h2>
              
              {userStats && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-800/50 rounded-xl p-6">
                      <h3 className="text-white font-bold mb-4 flex items-center">
                        <Target className="w-5 h-5 mr-2 text-neon-green" />
                        GOALS MASTERY
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Total Goals Set:</span>
                          <span className="text-white font-bold">{userStats.total_goals}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Completed:</span>
                          <span className="text-neon-green font-bold">{userStats.completed_goals}</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3">
                          <div 
                            className="bg-gradient-primary h-3 rounded-full transition-all duration-500"
                            style={{
                              width: userStats.total_goals > 0 
                                ? `${(userStats.completed_goals / userStats.total_goals) * 100}%` 
                                : '0%'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800/50 rounded-xl p-6">
                      <h3 className="text-white font-bold mb-4 flex items-center">
                        <Zap className="w-5 h-5 mr-2 text-electric-blue" />
                        ENERGY TRACKING
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Average Energy:</span>
                          <span className="text-white font-bold">{userStats.current_energy_avg.toFixed(1)}/10</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Total Sessions:</span>
                          <span className="text-electric-blue font-bold">{userStats.total_sessions}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Current Streak:</span>
                          <span className="text-neon-green font-bold">{userStats.streak_days} days</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800/50 rounded-xl p-6">
                    <h3 className="text-white font-bold mb-4">ACHIEVEMENT UNLOCKED</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className={`text-center p-4 rounded-lg ${userStats.total_sessions >= 1 ? 'bg-neon-green/20 text-neon-green' : 'bg-gray-700 text-gray-400'}`}>
                        <div className="text-2xl mb-1">🚀</div>
                        <div className="text-xs font-bold">FIRST CHAT</div>
                      </div>
                      <div className={`text-center p-4 rounded-lg ${userStats.total_sessions >= 10 ? 'bg-electric-blue/20 text-electric-blue' : 'bg-gray-700 text-gray-400'}`}>
                        <div className="text-2xl mb-1">💪</div>
                        <div className="text-xs font-bold">CHAT MASTER</div>
                      </div>
                      <div className={`text-center p-4 rounded-lg ${userStats.completed_goals >= 1 ? 'bg-neon-green/20 text-neon-green' : 'bg-gray-700 text-gray-400'}`}>
                        <div className="text-2xl mb-1">🎯</div>
                        <div className="text-xs font-bold">GOAL CRUSHER</div>
                      </div>
                      <div className={`text-center p-4 rounded-lg ${userStats.streak_days >= 7 ? 'bg-electric-blue/20 text-electric-blue' : 'bg-gray-700 text-gray-400'}`}>
                        <div className="text-2xl mb-1">🔥</div>
                        <div className="text-xs font-bold">WEEK WARRIOR</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;